import { io } from 'socket.io-client';
import { currentAppUrls } from '../config/urls';

class WebSocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.reconnectDelay = 1000;
    this.eventHandlers = new Map();
    this.connectionStartTime = null;
    this.lastError = null;
    this.isManuallyDisconnected = false;
    this.isAuthError = false;
    this.isConnecting = false;
    this.wakeLock = null;
    this.refreshEventLastEmittedAt = new Map();
    this.refreshEventMinIntervalMs = 1500;

    // Heartbeat and health tracking
    this.lastPongTime = null;
    this.lastPingTime = null;
    this.healthCheckInterval = null;
    this.pingInterval = 25000;
    this.pongTimeout = 5000;
    this.staleThreshold = 45000;
    this.reconnectBeforeTimeout = 15000;

    // Initialize event handlers with null values
    this.visibilityChangeHandler = null;
    this.onlineHandler = null;
    this.offlineHandler = null;
    this.focusHandler = null;
    this.blurHandler = null;
    this.unloadHandler = null;
  }

  // Bind event handlers after methods are defined
  bindEventHandlers() {
    this.visibilityChangeHandler = this.handleVisibilityChange.bind(this);
    this.onlineHandler = this.handleOnlineEvent.bind(this);
    this.offlineHandler = this.handleOfflineEvent.bind(this);
    this.focusHandler = this.handleFocusEvent.bind(this);
    this.blurHandler = this.handleBlurEvent.bind(this);
    this.unloadHandler = this.handleBeforeUnload.bind(this);
  }

  // Initialize the service
  init() {
    // Don't bind handlers immediately in constructor
    // Wait for all methods to be defined first
    this.bindEventHandlers();
  }


  /**
   * Get JWT token from sessionStorage (set by login response)
   * Falls back to cookie-based auth via withCredentials: true
   */
  getAuthToken() {
    // Try sessionStorage first (set by login/refresh)
    const token = sessionStorage.getItem('wsToken');
    if (token) {
      return token;
    }
    // Fallback: cookies are sent automatically with withCredentials: true
    return null;
  }

  /**
   * Update stored token when server sends refreshed token
   * This updates the sessionStorage token used for WebSocket authentication
   */
  updateStoredToken(newToken) {
    if (newToken) {
      sessionStorage.setItem('wsToken', newToken);
    }
  }

  /**
   * Connect to WebSocket server with JWT authentication and token refresh support
   */
  connect() {
    if (this.isConnecting) {
      return;
    }

    // CRITICAL FIX: If max attempts reached and not manually disconnected, allow forced reconnect
    const shouldAllowConnect = !this.isManuallyDisconnected && (this.isConnected || this.reconnectAttempts >= this.maxReconnectAttempts);

    // CRITICAL: Reset attempts counter when manually reconnecting after max reached
    // Don't reset if the last error was an auth error - reconnecting won't help
    if (!this.isAuthError && this.reconnectAttempts >= this.maxReconnectAttempts && !this.isManuallyDisconnected) {
      this.reconnectAttempts = 0;
    }

    if (this.socket && !this.socket.disconnected && !this.isManuallyDisconnected) {
      // CRITICAL FIX: Reduce console spam - only log in development
      if (process.env.NODE_ENV === 'development') {
      }
      return;
    }

    // CRITICAL FIX: Skip connection if not authenticated - prevents auth errors on login page
    const token = this.getAuthToken();
    if (!token) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[WebSocket] Skipping connection: No token available (user not authenticated)');
      }
      return;
    }

    // Check if browser is online
    if (!navigator.onLine) {
      // CRITICAL FIX: Reduce console spam - only log in development
      if (process.env.NODE_ENV === 'development') {
        console.warn('Browser is offline, skipping WebSocket connection');
      }
      this.handleConnectionError(new Error('Browser is offline'));
      return;
    }

    try {
      // CRITICAL FIX: Reduce console spam - only log in development
      const isDev = process.env.NODE_ENV === 'development';
      if (isDev) {
      }

      // Ensure we have a valid URL - CRITICAL FIX: Use the correct WebSocket URL with namespace
      let wsUrl = currentAppUrls.WEBSOCKET_URL;

      // If WebSocket URL is not configured, derive it from API_BASE
      if (!wsUrl) {
        wsUrl = currentAppUrls.API_BASE;
        if (isDev) {
        }
      }

      // Ensure proper protocol for WebSocket
      if (wsUrl && wsUrl.startsWith('https')) {
        wsUrl = wsUrl.replace('https', 'wss');
      } else if (wsUrl && wsUrl.startsWith('http')) {
        wsUrl = wsUrl.replace('http', 'ws');
      }

      // Remove trailing slash if present
      if (wsUrl && wsUrl.endsWith('/')) {
        wsUrl = wsUrl.slice(0, -1);
        if (isDev) {
        }
      }

      // Ensure the URL is the base URL without namespace appended here
      if (isDev) {
      }

      if (!wsUrl) {
        console.error('No WebSocket URL configured');
        this.handleConnectionError(new Error('No WebSocket URL configured'));
        return;
      }

      this.isManuallyDisconnected = false;
      this.isConnecting = true;
      this.connectionStartTime = Date.now();

      // Base URL should only be the host/protocol for Socket.IO constructor
      const baseWsUrl = wsUrl;
      if (isDev) {
      }

      // Token was already checked above, include it in auth
      if (isDev) {
        console.log('[WebSocket] Token found, attempting connection...');
      }

      this.socket = io(baseWsUrl, {
        path: '/socket.io/', // CRITICAL: Explicit path must match server configuration
        transports: ['polling'],
        timeout: 20000, // CRITICAL FIX: Increased timeout for better reliability
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 30000, // CRITICAL FIX: Increased max delay
        maxReconnectionAttempts: 10, // CRITICAL FIX: Increased reconnection attempts
        randomizationFactor: 0.5,
        forceNew: true, // CRITICAL FIX: Always create new connection for clean state
        autoConnect: true,
        multiplex: false, // CRITICAL FIX: Disable multiplexing for cleaner connections
        // Enhanced options for sleep/wake cycles
        pingTimeout: 60000, // 60 seconds
        pingInterval: 25000, // 25 seconds
        upgrade: false,
        // Authentication options
        auth: {
          token: token
        },
        // Include credentials for cookie-based auth
        withCredentials: true,
        rememberUpgrade: false,
        // Query parameters for debugging
        query: {
          clientVersion: '1.0.0',
          clientTime: new Date().toISOString()
        }
      });

      this.setupEventListeners();
      this.setupBrowserEventListeners();
    } catch (error) {
      this.isConnecting = false;
      console.error('WebSocket connection failed:', error);
      this.handleConnectionError(error);
    }
  }

  /**
   * Setup WebSocket event listeners
   */
  setupEventListeners() {
    if (!this.socket) return;

    // Throttling cache for status updates
    this.statusUpdateCache = new Map();
    const STATUS_THROTTLE_DELAY = 2000; // 2 seconds

    this.socket.on('connect', () => {
      // CRITICAL FIX: Reduce console spam - only log in development
      if (process.env.NODE_ENV === 'development') {
      }
      this.isConnected = true;
      this.isConnecting = false;
      this.reconnectAttempts = 0;
      this.isAuthError = false;
      this.lastPongTime = Date.now();
      this.emit('connected', { socketId: this.socket.id });
      this.startHeartbeat();
    });

    this.socket.onAny((eventName) => {
      if (eventName !== 'connect_error' && eventName !== 'error') {
        this.lastPongTime = Date.now();
      }
    });

    this.socket.on('disconnect', (reason) => {
      // CRITICAL FIX: Reduce console spam - only log in development
      if (process.env.NODE_ENV === 'development') {
      }
      this.isConnected = false;
      this.isConnecting = false;
      this.stopHeartbeat();
      this.emit('disconnected', { reason });
    });

    this.socket.on('connect_error', (error) => {
      console.error('❌ WebSocket connection failed:', error.message);
      this.isConnected = false;
      this.isConnecting = false;
      this.emit('connection_error', { error: error.message });
      this.handleConnectionError(error);
    });

    this.socket.on('error', (error) => {
      console.error('🚨 WebSocket error:', error);
      this.emit('error', { error });
    });

    // Dashboard statistics updates
    this.socket.on('notification:new', (data) => {
      this.emit('notification:new', data);
    });

    this.socket.on('stats_update', (data) => {
      // CRITICAL FIX: Reduce console spam - only log in development
      if (process.env.NODE_ENV === 'development') {
      }
      this.emitRefreshEvent('stats_update', data);
    });

    this.socket.on('submission_status_update', (data) => {
      this.emit('submission_status_update', data);
    });

    this.socket.on('buyer_email_status_update', (data) => {
      this.emit('buyer_email_status_update', data);
    });

    this.socket.on('buyer_email_worker_health', (data) => {
      this.emit('buyer_email_worker_health', data);
    });

    this.socket.on('admin_invoice_validated', (data) => {
      this.emit('admin_invoice_validated', data);
    });

    this.socket.on('admin_email_failed', (data) => {
      this.emit('admin_email_failed', data);
    });

    this.socket.on('admin_email_bounced', (data) => {
      this.emit('admin_email_bounced', data);
    });

    // Service status updates with throttling
    this.socket.on('service_status', (data) => {
      const payload = data.payload;
      const cacheKey = `${payload.status}_${payload.message}_${payload.connected}`;
      const now = Date.now();
      
      // Check if we should throttle this update
      if (this.statusUpdateCache.has(cacheKey)) {
        const lastUpdate = this.statusUpdateCache.get(cacheKey);
        if (now - lastUpdate < STATUS_THROTTLE_DELAY) {
          // CRITICAL FIX: Reduce console spam - only log in development
          if (process.env.NODE_ENV === 'development') {
          }
          return; // Skip this update
        }
      }
      
      // Update cache and emit
      this.statusUpdateCache.set(cacheKey, now);
      // CRITICAL FIX: Reduce console spam - only log in development
      if (process.env.NODE_ENV === 'development') {
      }
      this.emit('service_status', data);
    });

    // Import progress updates
    this.socket.on('import_progress', (data) => {
      // CRITICAL FIX: Reduce console spam - only log in development
      if (process.env.NODE_ENV === 'development') {
      }
      this.emit('import_progress', data);
    });

    // Detailed import progress updates (counts, current invoice, terminal completed/idle).
    // The backend broadcasts this HTTP-driven event (serviceRoutes broadcastDetailedImportProgress);
    // without this subscription the Dashboard's import_progress_detailed listener never fires, so the
    // real-time progress and the terminal completion signal are dropped and the card relies on slow
    // polling of a possibly-stale snapshot.
    this.socket.on('import_progress_detailed', (data) => {
      this.emit('import_progress_detailed', data);
    });

    // Import completion updates
    this.socket.on('import_complete', (data) => {
      // CRITICAL FIX: Reduce console spam - only log in development
      if (process.env.NODE_ENV === 'development') {
      }
      this.emitRefreshEvent('import_complete', data);
    });

    // LHDN sync result events
    this.socket.on('sync_complete', (data) => {
      this.emitRefreshEvent('sync_complete', data);
    });

    this.socket.on('sync_error', (data) => {
      this.emit('sync_error', data);
    });

    // Audit log updates (for admin users)
    this.socket.on('audit_log_update', (data) => {
      // CRITICAL FIX: Reduce console spam - only log in development
      if (process.env.NODE_ENV === 'development') {
      }
      this.emit('audit_log_update', data);
    });

    // Security alerts (for admin users)
    this.socket.on('security_alert', (data) => {
      // CRITICAL FIX: Reduce console spam - only log in development
      if (process.env.NODE_ENV === 'development') {
      }
      this.emit('security_alert', data);
    });

    // Handle token refresh from server
    this.socket.on('token_refreshed', (data) => {
      // CRITICAL FIX: Reduce console spam - only log in development
      if (process.env.NODE_ENV === 'development') {
      }
      if (data.token) {
        this.updateStoredToken(data.token);
        this.emit('token_refreshed', data);
      }
    });

    // Handle authentication errors
    this.socket.on('auth_error', (data) => {
      console.error('WebSocket authentication error:', data);
      this.emit('auth_error', data);
      if (data.code === 'SESSION_EXPIRED' || data.code === 'TOKEN_REVOKED') {
        // True invalid-credential cases - stop reconnecting and trigger logout
        this.isAuthError = true;
        this.emit('session_expired', data);
      } else {
        // CONCURRENT_SESSION: JWT was refreshed mid-session (new sessionId in DB).
        // The updated token in sessionStorage should match - attempt a fresh connect.
        setTimeout(() => {
          if (!this.isManuallyDisconnected && !this.isConnected) {
            this.reconnectAttempts = 0;
            this.connect();
          }
        }, 3000);
      }
    });

    // CRITICAL FIX: Handle import tracking reset event
    // This is triggered when user clicks "Reset Tracking" in the service control GUI
    this.socket.on('import_tracking_reset', (data) => {
      // CRITICAL FIX: Reduce console spam - only log in development
      if (process.env.NODE_ENV === 'development') {
      }
      this.emit('import_tracking_reset', data);
    });
  }

  /**
   * Setup browser event listeners for sleep/wake detection
   */
  setupBrowserEventListeners() {
    this.removeBrowserEventListeners();

    // Listen for visibility changes (tab switching, minimize/maximize)
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
    
    // Listen for online/offline events
    window.addEventListener('online', this.onlineHandler);
    window.addEventListener('offline', this.offlineHandler);
    
    // Listen for page focus/blur events
    window.addEventListener('focus', this.focusHandler);
    window.addEventListener('blur', this.blurHandler);

    // Listen for page beforeunload to clean up
    window.addEventListener('beforeunload', this.unloadHandler);
  }

  /**
   * Remove browser event listeners
   */
  removeBrowserEventListeners() {
    document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
    window.removeEventListener('online', this.onlineHandler);
    window.removeEventListener('offline', this.offlineHandler);
    window.removeEventListener('focus', this.focusHandler);
    window.removeEventListener('blur', this.blurHandler);
    window.removeEventListener('beforeunload', this.unloadHandler);
  }


  /**
   * Handle focus event
   */
  handleFocusEvent() {
    // Don't reset counter if the disconnect was caused by an auth error
    if (!this.isAuthError && this.reconnectAttempts >= this.maxReconnectAttempts && !this.isManuallyDisconnected) {
      this.reconnectAttempts = 0;
    }
    if (!this.isConnected && !this.isManuallyDisconnected && !this.isAuthError) {
      this.connect();
    }
  }


  /**
   * Handle blur event
   */
  handleBlurEvent() {
    // Don't disconnect on blur, let the connection persist
  }


  /**
   * Handle beforeunload event
   */
  handleBeforeUnload() {
    this.isManuallyDisconnected = true;
    this.removeBrowserEventListeners();
  }

  /**
   * Handle visibility change event (tab switching, minimize/maximize)
   */
  handleVisibilityChange() {
    if (document.hidden) {
      // Don't disconnect on hidden, let the connection persist
    } else {
      // Don't reset counter or reconnect if the disconnect was caused by an auth error
      if (!this.isAuthError && this.reconnectAttempts >= this.maxReconnectAttempts && !this.isManuallyDisconnected) {
        this.reconnectAttempts = 0;
      }
      if (!this.isConnected && !this.isManuallyDisconnected && !this.isAuthError) {
        this.connect();
      }
    }
  }


  /**
   * Handle online event
   */
  handleOnlineEvent() {
    // Don't reset counter or reconnect if the disconnect was caused by an auth error
    if (!this.isAuthError && this.reconnectAttempts >= this.maxReconnectAttempts && !this.isManuallyDisconnected) {
      this.reconnectAttempts = 0;
    }
    if (!this.isConnected && !this.isManuallyDisconnected && !this.isAuthError) {
      this.connect();
    }
  }

  /**
   * Handle offline event
   */
  handleOfflineEvent() {
    // CRITICAL FIX: Reduce console spam - only log in development
    if (process.env.NODE_ENV === 'development') {
      console.warn('Browser is offline - stopping WebSocket connection attempts.');
    }
    this.isConnected = false;
    this.handleConnectionError(new Error('Browser went offline'));
  }

  /**
   * Add event listener
   */
  on(event, callback) {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event).add(callback);
  }

  /**
   * Remove event listener
   */
  off(event, callback) {
    if (this.eventHandlers.has(event)) {
      const callbacks = this.eventHandlers.get(event);
      callbacks.delete(callback);
      if (callbacks.size === 0) {
        this.eventHandlers.delete(event);
      }
    }
  }

  /**
   * Emit event to registered callbacks
   */
  emit(event, data) {
    if (this.eventHandlers.has(event)) {
      const callbacks = this.eventHandlers.get(event);
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in event handler for ${event}:`, error);
        }
      });
    }
  }

  /**
   * Get connection status
   */
  getStatus() {
    return {
      isConnected: this.isConnected,
      socketId: this.socket?.id || null,
      reconnectAttempts: this.reconnectAttempts
    };
  }

  /**
   * Get current connection URL
   */
  getConnectionUrl() {
    return currentAppUrls.WEBSOCKET_URL;
  }

  /**
   * Method to manually trigger a status refresh via WebSocket
   */
  requestStatusRefresh() {
    if (this.socket && this.isConnected) {
      this.socket.emit('requestStatusRefresh');
      // CRITICAL FIX: Reduce console spam - only log in development
      if (process.env.NODE_ENV === 'development') {
      }
    } else {
      // CRITICAL FIX: Reduce console spam - only log in development
      if (process.env.NODE_ENV === 'development') {
        console.warn('[WebSocketService] Cannot request status refresh: not connected.');
      }
    }
  }

  /**
   * Method to trigger manual import via WebSocket
   */
  triggerManualImport(targetUsernames = null) {
    if (this.socket && this.isConnected) {
      this.socket.emit('triggerManualImport', { targetUsernames });
      // CRITICAL FIX: Reduce console spam - only log in development
      if (process.env.NODE_ENV === 'development') {
      }
    } else {
      // CRITICAL FIX: Reduce console spam - only log in development
      if (process.env.NODE_ENV === 'development') {
        console.warn('[WebSocketService] Cannot trigger manual import: not connected.');
      }
    }
  }

  /**
   * Method to test connection via WebSocket
   */
  testConnection() {
    if (this.socket && this.isConnected) {
      this.socket.emit('testConnection');
      // CRITICAL FIX: Reduce console spam - only log in development
      if (process.env.NODE_ENV === 'development') {
      }
    } else {
      // CRITICAL FIX: Reduce console spam - only log in development
      if (process.env.NODE_ENV === 'development') {
        console.warn('[WebSocketService] Cannot test connection: not connected.');
      }
    }
  }

  /**
   * Start heartbeat mechanism with health monitoring
   */
  startHeartbeat() {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
    }

    this.healthCheckInterval = setInterval(() => {
      this.checkConnectionHealth();
    }, 30000);
  }

  /**
   * Check connection health and trigger reconnect if stale
   */
  checkConnectionHealth() {
    if (!this.socket || !this.isConnected) {
      return;
    }

    if (this.socket.disconnected && !this.isManuallyDisconnected && !this.isAuthError) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[WebSocket] Socket disconnected during health check. Reconnecting.');
      }
      this.isConnected = false;
      this.isConnecting = false;
      this.connect();
    }
  }

  emitRefreshEvent(event, data) {
    const now = Date.now();
    const lastEmittedAt = this.refreshEventLastEmittedAt.get(event) || 0;
    if (now - lastEmittedAt < this.refreshEventMinIntervalMs) {
      if (process.env.NODE_ENV === 'development') {
        console.info('[WebSocket] Refresh event ignored during throttle window', {
          event,
          elapsedMs: now - lastEmittedAt
        });
      }
      return;
    }

    this.refreshEventLastEmittedAt.set(event, now);
    this.emit(event, data);
  }

  /**
   * Stop heartbeat mechanism
   */
  stopHeartbeat() {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }
    this.lastPongTime = null;
    this.lastPingTime = null;
  }

  /**
   * Handle connection error with improved logic
   */
  handleConnectionError(error) {
    this.lastError = error;
    this.emit('connection_error', { error: error.message || error });

    // Don't attempt reconnection if manually disconnected
    if (this.isManuallyDisconnected) {
      // CRITICAL FIX: Reduce console spam - only log in development
      if (process.env.NODE_ENV === 'development') {
      }
      return;
    }

    // Don't attempt reconnection if browser is offline
    if (!navigator.onLine) {
      // CRITICAL FIX: Reduce console spam - only log in development
      if (process.env.NODE_ENV === 'development') {
      }
      return;
    }

    // Detect auth errors from connect_error - stop retrying and emit auth events
    const errMsg = (error.message || '').toLowerCase();
    if (errMsg.includes('authentication error') || errMsg.includes('session expired') || errMsg.includes('token reuse') || errMsg.includes('token has been revoked') || errMsg.includes('invalid token')) {
      this.isAuthError = true;
      this.emit('auth_error', { message: error.message, code: 'SESSION_EXPIRED' });
      this.emit('session_expired', { message: error.message, code: 'SESSION_EXPIRED' });
      return; // Don't retry on auth errors
    }

    // Helper: reconnect (HTTP layer handles token refresh independently)
    const reconnectWithTokenRefresh = async () => {
      if (this.isManuallyDisconnected || this.isConnected || this.isAuthError) return;
      this.connect();
    };

    // Don't attempt reconnection if connection was very recent (likely a transient error)
    const connectionDuration = Date.now() - this.connectionStartTime;
    if (connectionDuration < 5000 && this.reconnectAttempts === 0) {
      // CRITICAL FIX: Reduce console spam - only log in development
      if (process.env.NODE_ENV === 'development') {
      }
      setTimeout(() => {
        reconnectWithTokenRefresh();
      }, 3000);
      return;
    }

    // Exponential backoff for reconnection attempts
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);

      // CRITICAL FIX: Reduce console spam - only log in development
      if (process.env.NODE_ENV === 'development') {
      }

      setTimeout(() => {
        reconnectWithTokenRefresh();
      }, delay);
    } else {
      // CRITICAL FIX: Reduce console spam - only log in development
      if (process.env.NODE_ENV === 'development') {
        console.error('[WebSocketService] Max reconnection attempts reached');
      }
      this.emit('max_reconnect_attempts', { error });
    }
  }

  /**
   * Disconnect WebSocket connection
   */
  disconnect() {
    if (this.socket) {
      this.isManuallyDisconnected = true;
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.isConnecting = false;
      this.reconnectAttempts = 0;
      this.isAuthError = false;
      this.stopHeartbeat();
      this.removeBrowserEventListeners();
    }
  }

  /**
   * Force reconnect WebSocket (called by user action like button click)
   */
  forceReconnect() {
    this.isManuallyDisconnected = false;
    this.reconnectAttempts = 0;
    this.isAuthError = false;
    this.lastError = null;
    this.isConnected = false;
    this.isConnecting = false;
    this.stopHeartbeat();
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.connect();
  }
}

// Create singleton instance
const websocketService = new WebSocketService();

// Initialize the service
websocketService.init();

// Auto-connect when service is imported
if (typeof window !== 'undefined') {
  // Only auto-connect in browser environment
  setTimeout(() => {
    websocketService.connect();
  }, 1000);
}

export default websocketService;
